<template>
  <div id="vue-the-product-list" :class="bemBlock">
    <TheProductListHeader
      :breadcrumbs="breadcrumbs"
      :breadcrumb-not-as-h1="breadcrumbNotAsH1"
    />
    <TheProductListFilters
      :class="bemElement('filters')"
      @filter-applied="() => (filterApplied = true)"
      @filter-cleared="() => (filterApplied = false)"
    />
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="categoryTopMessage"
      :class="bemElement('top-message')"
      v-html="categoryTopMessage"
    />
    <!-- eslint-enable vue/no-v-html -->
    <BaseProductGrid
      v-show="productSearchHasProducts"
      :analytics="{
        category,
        location: analyticsConst.location.plpGrid,
      }"
      :class="bemElement('product-grid')"
      :column-mode="columnMode"
      :products="products"
      type="productList"
      :bottom-spinner="bottomSpinner"
      :header-height="headerHeight"
      :infinite-next="!btnNextPageEnabled"
      :infinite-previous="true"
      @infinite:next="handleCaptureNextPage"
      @infinite:previous="previousPage"
      @category-shown="handleCategoryShown"
    >
      <template #afterList>
        <p
          v-show="isLastPage"
          :class="[
            bemElement('description'),
            { 'visually-hidden': !isLastPage },
          ]"
          v-html="description"
        />
        <div
          v-if="
            btnNextPageEnabled &&
            currentStateNextPage &&
            productSearchHasProducts &&
            !isLastPage
          "
          :class="bemElement('container-next-page')"
        >
          <BaseSpinner v-show="loading" inline />
          <button
            v-show="!loading"
            :class="[bemElement('btn-next-page'), 'btn']"
            :aria-label="
              $msg('productList.nextPageBtnDescription', 'productList')
            "
            @click="nextPage(currentStateNextPage)"
          >
            {{ $msg('productList.nextPageBtn', 'productList') }}
          </button>
        </div>
      </template>
    </BaseProductGrid>
    <div
      v-if="!loading && !productSearchHasProducts"
      :class="[bemElement('empty')]"
    >
      <p :class="[bemElement('empty-text')]">
        {{ $msg('productList.empty.text', 'productList') }}
      </p>
      <button
        :class="[
          bemElement('empty-button'),
          'btn',
          {
            'btn--cta': isBreakpointMobile,
            'btn-primary': !isBreakpointMobile,
          },
        ]"
        @click="handleEmptyButtonClick"
      >
        {{ $msg('productList.clearfilters.text', 'productList') }}
      </button>
    </div>
  </div>
</template>
<script>
import Axios from 'axios';
import debounce from 'lodash/debounce';
import { mapGetters, mapState } from 'vuex';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { defaultInfiniteScrollMixin } from '../mixins/infiniteScrollMixin';
import pageDataMixin from '../mixins/pageDataMixin';
import productSearchLatestProductIdsMixin from '../mixins/productSearchLatestProductIdsMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import BaseProductGrid from './BaseProductGrid.vue';
import BaseSpinner from './BaseSpinner.vue';
import TheProductListFilters from './TheProductListFilters.vue';
import TheProductListHeader from './TheProductListHeader.vue';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import getFrontConfig from '../../common/getFrontConfig';
import { isLastPage } from '../utils/productSearch';
import store, { getters, types } from '../store';
import { routerReplaceAndIgnore } from '../router';
import EventBusConfig from '../config/eventBusConfig';
import { PAGE_TYPES } from '../../../../../config/analytics';
import analyticsMixin from '../mixins/analyticsMixin';

const analyticsEventBus = getAnalyticsEventBus();
const FRONT_CONFIG = getFrontConfig();

export default {
  name: 'TheProductList',

  components: {
    BaseProductGrid,
    BaseSpinner,
    TheProductListFilters,
    TheProductListHeader,
  },

  mixins: [
    analyticsMixin,
    bemClassesMixin,
    defaultInfiniteScrollMixin,
    pageDataMixin,
    productSearchLatestProductIdsMixin,
    windowResizeMixin,
  ],

  data() {
    return {
      bottomSpinner: null,
      btnNextPageEnabled: false,
      currentStateNextPage: false,
      dataChangeBreakpoint: FRONT_CONFIG.responsive.bp_575,
      getContentAssetUrl: FRONT_CONFIG.urls.getContentAssetUrl,
      categoryTopMessage: null,
      breadcrumbNotAsH1: null,
      filterApplied: false,
    };
  },

  computed: {
    ...mapGetters({
      products: getters.productList.products,
      category: getters.productList.visibleCategory,
      headerHeights: getters.header.getHeights,
      breadcrumbs: getters.categories.currentParentCategories,
    }),
    ...mapState({
      columnMode: (state) => state.productList.ui.columnMode,
      isProductListVisible: (state) => !state.productList.ui.hidden,
      productSearch: (state) => state.productList.productSearch,
      loading: (state) => state.productList.loading,
    }),
    headerHeight() {
      return this.headerHeights?.headerPlusBanner ?? 0;
    },
    heading() {
      return this.category?.name || 'Loading...';
    },
    description() {
      return this.category?.description || '';
    },
    productSearchHasProducts() {
      return (
        (this.filterApplied && this.productSearch?.count > 0) ||
        (!this.filterApplied &&
          (this.productSearch?.category?.bylBanners?.length > 0 ||
            this.productSearch?.count > 0))
      );
    },
    isLastPage() {
      if (this.productSearch) {
        return isLastPage(this.productSearch);
      }
      return false;
    },
  },

  watch: {
    category: debounce(function productListUpdateHandler() {
      this.$nextTick(() => {
        if (this.isProductListVisible) {
          this.loadTopMessage();
          this.catBreadcrumbNotAsH1();
          this.$eventBus.$emit(
            EventBusConfig.events.TheProductList.categoryChanged,
            this.category
          );
          this.pageTitle = this.category.pageTitle;
          this.pageType = PAGE_TYPES.category;
        }
      });
    }, 1000),
    products(newProducts, oldProducts) {
      if (newProducts.length > oldProducts.length) {
        this.sendAnalyticsEventViewed();
      }
      if (!this.loading && newProducts.length === 0 && this.category !== null) {
        this.sendAnalyticsEventViewedEmpty();
      }
    },
    productSearchLatestProductIds() {
      this.emitCQuotientData();
    },
  },

  mounted() {
    this.pageType = PAGE_TYPES.category;
    if (this.products?.length > 0) {
      this.loadTopMessage();
      this.catBreadcrumbNotAsH1();
      this.sendAnalyticsEventViewed();
      this.emitCQuotientData();
    } else {
      this.sendAnalyticsEventViewedEmpty();
    }
    this.changeBtnNextPageState();
    document.addEventListener('INDChangeOptions', this.changeBtnNextPageState);
  },

  beforeDestroy() {
    document.removeEventListener(
      'INDChangeOptions',
      this.changeBtnNextPageState
    );
  },

  methods: {
    changeBtnNextPageState() {
      this.btnNextPageEnabled = !!window.INDTextReaderEnabled;
    },
    handleCategoryShown(category) {
      // update current category in Vuex
      this.$store.dispatch(types.categories.setCategory, this.category);
      // update url
      routerReplaceAndIgnore(category.url);
    },
    handleEmptyButtonClick() {
      this.$eventBus.$emit(EventBusConfig.commands.TheProductList.clearFilters);
    },
    handleCaptureNextPage($state) {
      this.currentStateNextPage = $state;
      if (!this.btnNextPageEnabled) this.nextPage($state);
    },
    nextPage($state) {
      const { DIRECTION_BOTTOM } = this.infinite.constants;
      this.infiniteHandle(
        $state,
        (url, { nextPageCategory, willCategoryChangeInNextPage }) => {
          store.dispatch(types.productList.loadPageFromUrl, {
            url,
            minLoadingTime: willCategoryChangeInNextPage
              ? FRONT_CONFIG.infiniteScroll
                  ?.minMillisecondsWhenLoadingNewCategory
              : 0,
          });
          if (willCategoryChangeInNextPage) {
            this.bottomSpinner = `Loading ${nextPageCategory.name}`;
          } else {
            this.bottomSpinner = null;
          }
        },
        DIRECTION_BOTTOM
      );
    },
    previousPage($state) {
      const { DIRECTION_TOP } = this.infinite.constants;
      this.infiniteHandle(
        $state,
        (url) =>
          store.dispatch(types.productList.loadPageFromUrl, {
            url,
            prefetchAdjacent: false,
          }),
        DIRECTION_TOP
      );
    },
    sendAnalyticsEventViewed() {
      analyticsEventBus.publish(analyticsEventBus.EVENTS.productListViewed, {
        category: this.category,
        products: [...this.products],
      });
    },
    sendAnalyticsEventViewedEmpty() {
      if (this.category?.id) {
        analyticsEventBus.publish(
          analyticsEventBus.EVENTS.productListViewedEmpty,
          {
            category: this.category,
          }
        );
      }
    },
    emitCQuotientData() {
      if (window.bylCQuotient && this.category?.id) {
        window.bylCQuotient.trackViewProductInCategory(
          [...this.productSearchLatestProductIds],
          {
            category: this.category,
            refinements: this.productSearch?.selectedFilters,
            sortingRule: this.productSearch?.productSort?.ruleId,
          }
        );
      }
    },
    loadTopMessage() {
      if (this.category?.catTopMessageShow) {
        const url = this.getContentAssetUrl;
        Axios.get(url, {
          params: {
            id: 'topCategoryMessage',
          },
        })
          .then((response) => {
            this.categoryTopMessage = response.data?.content;
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error.response?.data?.message);
          });
      } else {
        this.categoryTopMessage = null;
      }
    },
    catBreadcrumbNotAsH1() {
      if (this.category?.catBreadcrumbNotAsH1) {
        this.breadcrumbNotAsH1 = this.category.catBreadcrumbNotAsH1;
      } else {
        this.breadcrumbNotAsH1 = null;
      }
    },
  },
};
</script>
